// src/pages/admin/AnalyticsDashboard.js
import React, { useState, useEffect } from 'react';
import { mockedData } from '../../data/mockedData';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FiBarChart2, FiMessageCircle, FiUsers, FiArrowUpRight, FiArrowDownRight } from 'react-icons/fi';



import useTrackSessionDuration from '../../components/admin/TrackSession';
import useTrackBounceRate from '../../components/admin/TrackBounceRate';

import TotalVisitorsChart from '../../components/admin/TotalVisitorsChart';

const AnalyticsDashboard = () => {
  const [visitorData, setVisitorData] = useState({});
  const [trafficData, setTrafficData] = useState({});
  const [sessionData, setSessionData] = useState(0);
  const [geographyData, setGeographyData] = useState({});
  const [bounceData, setBounceData] = useState({});
  const [totalVisitorsData, setTotalVisitorsData] = useState({});

  useEffect(() => {
    setVisitorData(mockedData.visitorData);
    setTrafficData(mockedData.trafficData);
    setSessionData(mockedData.sessionData.sessionDuration);
    setGeographyData(mockedData.geographyData);
    setBounceData(mockedData.bounceData);
    setTotalVisitorsData(mockedData.totalVisitorsData);
  }, []);

  useTrackSessionDuration();
  useTrackBounceRate();

  const trafficSourceData = [
    { name: 'Referral', value: trafficData.referral },
    { name: 'Organic Search', value: trafficData.organicSearch },
    { name: 'Paid Search', value: trafficData.paidSearch },
    { name: 'Direct', value: trafficData.direct }
  ];

  const sessionDurationData = [{ name: 'Session Duration', value: sessionData }];
  const geographyDataPie = [
    { name: geographyData.country, value: 1 },
    { name: geographyData.region, value: 1 },
    { name: geographyData.city, value: 1 }
  ];

  const trackVisitorsData = [
    { name: 'Unique Visitors', value: visitorData.unique },
    { name: 'Repeat Visitors', value: visitorData.repeat }
  ];

  const donutColor = '#4c6e91'; // Soft blue
  const highlightColor = '#6e84a3'; // Slightly lighter blue for accents

  return (
    <main className="flex-1 p-8 bg-neutral-950 min-h-screen">
      <h1 className="text-3xl font-bold text-white mb-6">Analytics Dashboard</h1>
      <p className="text-gray-400 mb-6">Explore visitor data, traffic sources, and performance insights.</p>

      {/* Metrics Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        
        {/* Total Visitors */}
        <div className="bg-neutral-900 p-6 rounded-xl shadow-xl border border-neutral-800 hover:shadow-2xl transition duration-300">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-white">Total Visitors</h4>
            <FiUsers className="text-white text-3xl" />
          </div>
          <TotalVisitorsChart data={totalVisitorsData} />
        </div>

        {/* Traffic Sources Donut Chart */}
        <div className="bg-neutral-900 p-6 rounded-xl shadow-xl border border-neutral-800 hover:shadow-2xl transition duration-300">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-white">Traffic Sources</h4>
            <FiBarChart2 className="text-white text-3xl" />
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={trafficSourceData}
                dataKey="value"
                nameKey="name"
                innerRadius={60} // Donut chart hole
                outerRadius={80}
                fill={donutColor}
                label
              >
                {trafficSourceData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={highlightColor} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Bounce Rate Donut Chart */}
        <div className="bg-neutral-900 p-6 rounded-xl shadow-xl border border-neutral-800 hover:shadow-2xl transition duration-300">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-white">Bounce Rate</h4>
            <FiArrowDownRight className="text-red-500 text-3xl" />
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[{ name: 'Bounced', value: bounceData.bounce }, { name: 'No Bounce', value: bounceData.noBounce }]}
                dataKey="value"
                nameKey="name"
                innerRadius={60}
                outerRadius={80}
                fill={donutColor}
                label
              >
                {['#f39c12', '#27ae60'].map((color, index) => (
                  <Cell key={`cell-${index}`} fill={color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Session Duration and Track Visitors */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        
        {/* Session Duration Bar Chart */}
        <div className="bg-neutral-900 p-6 rounded-xl shadow-xl border border-neutral-800 hover:shadow-2xl transition duration-300">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-white">Session Duration</h4>
            <FiArrowUpRight className="text-green-500 text-3xl" />
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={sessionDurationData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2c2c2c" />
              <XAxis dataKey="name" stroke="#999" />
              <YAxis stroke="#999" />
              <Tooltip />
              <Bar dataKey="value" fill={highlightColor} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Track Visitors Donut Chart */}
        <div className="bg-neutral-900 p-6 rounded-xl shadow-xl border border-neutral-800 hover:shadow-2xl transition duration-300">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-white">Track Visitors</h4>
            <FiUsers className="text-white text-3xl" />
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={trackVisitorsData}
                dataKey="value"
                nameKey="name"
                innerRadius={60}
                outerRadius={80}
                fill={donutColor}
                label
              >
                {trackVisitorsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={highlightColor} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* Geolocation Data */}
      <div className="mt-6 bg-neutral-900 p-6 rounded-xl shadow-xl border border-neutral-800 hover:shadow-2xl transition duration-300">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-white">Geolocation Data</h4>
          <FiMessageCircle className="text-white text-3xl" />
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={geographyDataPie}
              dataKey="value"
              nameKey="name"
              innerRadius={60}
              outerRadius={80}
              fill={donutColor}
              label
            >
              {geographyDataPie.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={highlightColor} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </main>
  );
};

export default AnalyticsDashboard;
